import { Capacitor } from "@capacitor/core"
import { LocalNotifications } from "@capacitor/local-notifications"
import { Preferences } from "@capacitor/preferences"
import { fetchWithAuth } from "@/utils/api-client"
import { isOnline } from "@/utils/networkBus"
import { GV_NOTIFICATION_SOUND } from "@/utils/notif"
import {
	MEAL_REMINDER_CHANNEL_ID,
	MEAL_REMINDER_DEFINITIONS,
	MEAL_REMINDER_KIND,
	buildMealReminderSchedule,
	createDefaultMealReminderSettings,
	hasEnabledMealReminders,
	isMealReminderNotificationId,
	normalizeMealReminderSettings,
	toLocalDateKey,
} from "@/utils/mealReminderCore"

const PREFERENCE_PREFIX = "gv-meal-reminders-v1"

function preferenceKey(userId) {
	return `${PREFERENCE_PREFIX}:${String(userId)}`
}

export async function loadMealReminderSettings(userId) {
	if (!userId) return createDefaultMealReminderSettings()

	try {
		const { value } = await Preferences.get({ key: preferenceKey(userId) })
		if (!value) return createDefaultMealReminderSettings()
		return normalizeMealReminderSettings(JSON.parse(value))
	} catch (error) {
		console.warn("Unable to load meal reminder settings", error)
		return createDefaultMealReminderSettings()
	}
}

export async function saveMealReminderSettings(userId, settings) {
	const normalized = normalizeMealReminderSettings(settings)
	if (!userId) return normalized

	await Preferences.set({
		key: preferenceKey(userId),
		value: JSON.stringify(normalized),
	})
	return normalized
}

export async function getMealReminderPermissionStatus() {
	if (!Capacitor.isNativePlatform()) return "unsupported"

	try {
		const status = await LocalNotifications.checkPermissions()
		return status.display
	} catch (error) {
		console.warn("Unable to check meal reminder permission", error)
		return "denied"
	}
}

export async function requestMealReminderPermission() {
	if (!Capacitor.isNativePlatform()) return "unsupported"

	try {
		const status = await LocalNotifications.requestPermissions()
		return status.display
	} catch (error) {
		console.warn("Unable to request meal reminder permission", error)
		return "denied"
	}
}

async function pendingMealReminderDescriptors(predicate = () => true) {
	if (!Capacitor.isNativePlatform()) return []

	const pending = await LocalNotifications.getPending()
	return pending.notifications
		.filter(
			(notification) =>
				isMealReminderNotificationId(notification.id) &&
				predicate(notification)
		)
		.map((notification) => ({ id: notification.id }))
}

export async function cancelPendingMealReminders(predicate) {
	if (!Capacitor.isNativePlatform()) return

	const notifications = await pendingMealReminderDescriptors(predicate)
	if (!notifications.length) return
	await LocalNotifications.cancel({ notifications })
}

async function ensureMealReminderChannel() {
	if (Capacitor.getPlatform() !== "android") return

	const channels = await LocalNotifications.listChannels()
	if (
		channels.channels.some(
			(channel) => channel.id === MEAL_REMINDER_CHANNEL_ID
		)
	) {
		return
	}

	await LocalNotifications.createChannel({
		id: MEAL_REMINDER_CHANNEL_ID,
		name: "Meal reminders",
		description: "Optional reminders to log meals in Grub Vision.",
		importance: 3,
		visibility: 1,
		vibration: false,
		lights: false,
		sound: GV_NOTIFICATION_SOUND,
	})
}

async function fetchMealReminderStatus() {
	if (!isOnline.value) return null

	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
	const localDate = toLocalDateKey(new Date())
	const response = await fetchWithAuth(
		`/api/meal-reminder-status?date=${encodeURIComponent(localDate)}`,
		{
			method: "GET",
			cache: "no-store",
			headers: {
				"User-Timezone": timezone,
			},
		}
	)

	if (!response.ok) {
		throw new Error(`Meal reminder status failed with ${response.status}`)
	}

	return response.json()
}

export async function reconcileMealReminders(userId) {
	if (!Capacitor.isNativePlatform() || !userId) {
		return { state: "unsupported" }
	}

	const settings = await loadMealReminderSettings(userId)
	if (!settings.introSeen) return { state: "awaiting-intro", settings }

	if (!hasEnabledMealReminders(settings)) {
		await cancelPendingMealReminders()
		return { state: "disabled", settings }
	}

	const permission = await getMealReminderPermissionStatus()
	if (permission !== "granted") {
		await cancelPendingMealReminders()
		return { state: "blocked", permission, settings }
	}

	let status
	try {
		status = await fetchMealReminderStatus()
	} catch (error) {
		console.warn("Unable to refresh meal reminder status", error)
		return { state: "status-unavailable", permission, settings }
	}

	if (!status) {
		return { state: "offline", permission, settings }
	}

	const schedule = buildMealReminderSchedule({
		settings,
		status,
		now: new Date(),
	})

	await cancelPendingMealReminders()
	await ensureMealReminderChannel()

	if (schedule.length) {
		const isAndroid = Capacitor.getPlatform() === "android"
		await LocalNotifications.schedule({
			notifications: schedule.map((entry) => ({
				id: entry.id,
				title: entry.title,
				body: entry.body,
				schedule: { at: entry.at },
				autoCancel: true,
				...(isAndroid ? { channelId: MEAL_REMINDER_CHANNEL_ID } : {}),
				extra: {
					kind: MEAL_REMINDER_KIND,
					reminderKey: entry.reminderKey,
					mealType: entry.mealType,
					date: entry.dateKey,
				},
			})),
		})
	}

	return {
		state: "scheduled",
		count: schedule.length,
		permission,
		settings,
	}
}

function reminderKeyForMeal(mealType, mealDate) {
	const normalized = String(mealType ?? "").toLowerCase()
	if (normalized === "snack" || normalized === "dessert") return "snacks"
	if (["breakfast", "lunch", "dinner"].includes(normalized)) return normalized

	const hour = mealDate.getHours()
	if (hour >= 5 && hour < 11) return "breakfast"
	if (hour >= 11 && hour < 16) return "lunch"
	if (hour >= 16 && hour < 22) return "dinner"
	return "snacks"
}

export async function cancelTodayMealRemindersForEntry({
	mealType,
	mealDateTime,
}) {
	if (!Capacitor.isNativePlatform()) return

	const mealDate = mealDateTime ? new Date(mealDateTime) : new Date()
	if (Number.isNaN(mealDate.getTime())) return

	const todayKey = toLocalDateKey(new Date())
	if (toLocalDateKey(mealDate) !== todayKey) return

	const reminderKey = reminderKeyForMeal(mealType, mealDate)
	await cancelPendingMealReminders((notification) => {
		const extra = notification.extra ?? {}
		return (
			extra.date === todayKey &&
			[reminderKey, "dailyCatchup"].includes(extra.reminderKey)
		)
	})
}

export async function registerMealReminderTapHandler(router) {
	if (!Capacitor.isNativePlatform()) return null

	return LocalNotifications.addListener(
		"localNotificationActionPerformed",
		async ({ notification }) => {
			const extra = notification?.extra ?? {}
			if (extra.kind !== MEAL_REMINDER_KIND) return

			const definition = MEAL_REMINDER_DEFINITIONS.find(
				(candidate) => candidate.key === extra.reminderKey
			)
			const mealType = extra.mealType ?? definition?.routeMealType
			await router.push({
				name: "add",
				...(mealType ? { query: { mealType } } : {}),
			})
		}
	)
}
